import { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Tabs, Tab, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useOffensiveStore from '../../stores/offensiveStore';
import { getTechniqueById } from '../../data/mitreAttack';
import './TerminalOutput.css';

const TerminalTab = ({ technique, isSelected, onSelect, onClose }) => {
  const techniqueData = getTechniqueById(technique.techniqueId);

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: isSelected ? '2px solid #10b981' : '2px solid transparent',
        background: isSelected ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          background: isSelected ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)',
        }
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.75rem',
          fontWeight: 600,
          whiteSpace: 'nowrap'
        }}
      >
        {technique.techniqueId}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: '0.7rem',
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {techniqueData?.name || technique.name}
      </Typography>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        sx={{
          ml: 'auto',
          color: 'rgba(255, 255, 255, 0.4)',
          '&:hover': {
            color: '#ef4444',
            background: 'rgba(239, 68, 68, 0.1)'
          }
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

const TerminalOutput = () => {
  const {
    techniques,
    selectedTerminal,
    setSelectedTerminal,
    terminalOutputs,
    clearTerminalOutput
  } = useOffensiveStore();

  const terminalRef = useRef(null);
  const shouldAutoScroll = useRef(true);

  // Get techniques that have terminal output
  const techniquesWithOutput = techniques.filter(
    t => t.status === 'running' || t.status === 'completed' || t.status === 'failed'
  );

  // Auto-select first technique if none selected
  useEffect(() => {
    if (!selectedTerminal && techniquesWithOutput.length > 0) {
      setSelectedTerminal(techniquesWithOutput[0].id);
    }
  }, [techniquesWithOutput.length, selectedTerminal, setSelectedTerminal]);

  // Auto-scroll to bottom when new output arrives
  useEffect(() => {
    if (terminalRef.current && shouldAutoScroll.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutputs, selectedTerminal]);

  const handleScroll = () => {
    if (terminalRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = terminalRef.current;
      // Check if user is near the bottom (within 100px)
      shouldAutoScroll.current = scrollHeight - scrollTop - clientHeight < 100;
    }
  };

  const handleCopyOutput = () => {
    const output = terminalOutputs[selectedTerminal]?.output || '';
    navigator.clipboard.writeText(output);
  };

  const handleDownloadOutput = () => {
    const output = terminalOutputs[selectedTerminal]?.output || '';
    const technique = techniques.find(t => t.id === selectedTerminal);
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${technique?.techniqueId || 'output'}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const selectedTechnique = techniques.find(t => t.id === selectedTerminal);
  const output = terminalOutputs[selectedTerminal]?.output || '';

  // Parse ANSI codes and colorize output
  const parseOutput = (text) => {
    if (!text) return [];
    
    const lines = text.split('\n');
    return lines.map((line, index) => {
      // Highlight important patterns
      let className = '';
      if (line.includes('ERROR') || line.includes('FAIL')) {
        className = 'terminal-error';
      } else if (line.includes('SUCCESS') || line.includes('FOUND') || line.includes('VULNERABLE')) {
        className = 'terminal-success';
      } else if (line.includes('WARNING')) {
        className = 'terminal-warning';
      } else if (line.startsWith('$') || line.startsWith('#') || line.startsWith('root@')) {
        className = 'terminal-prompt';
      }

      return (
        <div key={index} className={`terminal-line ${className}`}>
          {line || '\u00A0'}
        </div>
      );
    });
  };

  return (
    <Box className="terminal-output-container">
      {/* Tabs */}
      {techniquesWithOutput.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'rgba(15, 23, 42, 0.8)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(99, 126, 234, 0.5)',
              borderRadius: '2px',
            },
          }}
        >
          {techniquesWithOutput.map((technique) => (
            <TerminalTab
              key={technique.id}
              technique={technique}
              isSelected={selectedTerminal === technique.id}
              onSelect={() => setSelectedTerminal(technique.id)}
              onClose={() => {
                // Close tab and select another
                const newTechniques = techniquesWithOutput.filter(t => t.id !== technique.id);
                if (newTechniques.length > 0) {
                  setSelectedTerminal(newTechniques[0].id);
                } else {
                  setSelectedTerminal(null);
                }
                clearTerminalOutput(technique.id);
              }}
            />
          ))}
        </Box>
      )}

      {/* Terminal Header */}
      {selectedTechnique && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            background: 'rgba(0, 0, 0, 0.4)',
            borderBottom: '1px solid rgba(16, 185, 129, 0.3)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: selectedTechnique.status === 'running' ? '#10b981' : '#6b7280',
                animation: selectedTechnique.status === 'running' ? 'blink 1.5s infinite' : 'none',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#10b981',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {selectedTechnique.agent || 'kali-agent'} {selectedTechnique.tool ? `[${selectedTechnique.tool}]` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Copy Output">
              <IconButton
                size="small"
                onClick={handleCopyOutput}
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  '&:hover': { color: '#fff' }
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Output">
              <IconButton
                size="small"
                onClick={handleDownloadOutput}
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  '&:hover': { color: '#fff' }
                }}
              >
                <GetAppIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Terminal Output */}
      <Box
        ref={terminalRef}
        onScroll={handleScroll}
        className="terminal-content"
        sx={{
          flex: 1,
          background: '#0a0e1a',
          color: '#10b981',
          fontFamily: '"Fira Code", "Courier New", monospace',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          p: 2,
          overflowY: 'auto',
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          minHeight: '200px',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.3)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(16, 185, 129, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(16, 185, 129, 0.5)',
            },
          },
        }}
      >
        {selectedTerminal && output ? (
          parseOutput(output)
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'rgba(16, 185, 129, 0.3)',
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {techniquesWithOutput.length === 0
                ? '$ Waiting for techniques to execute...'
                : '$ No output yet...'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TerminalOutput;

